"use client";

import { useState } from "react";
import {
  Plus,
  Minus,
  Building2,
  Gavel,
  Scale,
  Briefcase,
  Home,
  Copyright,
  Banknote,
  Users,
  Plane,
  Leaf,
  Quote
} from "lucide-react";
import type { Service } from "../../data/serviceData";
import SocialIcons from "@/components/frontend/expertise/SocialIcons";

// map slugs → icons
const iconMap: Record<string, any> = {
  "corporate-commercial-law": Building2,
  "litigation-dispute-resolution": Gavel,
  "constitutional-administrative-law": Scale,
  "employment-labour-law": Briefcase,
  "property-real-estate": Home,
  "intellectual-property": Copyright,
  "banking-finance": Banknote,
  "family-law": Users,
  "immigration-law": Plane,
  "energy-natural-resources-environmental-law": Leaf,
};

export default function SingleService({ service }: { service: Service }) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggleAccordion = (idx: number) => {
    setOpenIndex(openIndex === idx ? null : idx);
  };

  const Icon = iconMap[service.slug];

  return (
    <div className="relative w-full min-h-screen">
      {/* Background with overlay */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: `url(${service.featuredImage})`,
          backgroundAttachment: "fixed",
          backgroundPosition: "center center",
        }}
      >
        <div className="absolute inset-0 bg-primary/65 z-0"></div>

        <div className="fixed inset-0 flex items-start justify-center pt-50 z-10">
          <div className="flex flex-col items-center justify-center">
            <h1 className="text-white text-[40px] md:text-[52px] font-lora leading-12 md:leading-14 font-bold text-center mb-10 gap-4 w-[80%]">{service.title}</h1>
            {/* <hr className="border-2 border-white w-[40%] mt-3 mb-10" /> */}

            {service.quote && service.quote.length > 0 && (
              <blockquote className="text-white text-[18px] italic text-center w-[60%] mt-4 relative">
                {/* Quotation Icon */}
                <Quote className="absolute -top-8 -left-10 rotate-180 transform -translate-x-1/2 w-8 h-8 text-white/70" />
                  {service.quote[0].message}
                <Quote className="absolute -bottom-6 -right-10 transform -translate-x-1/2 w-8 h-8 text-white/70" />
                <footer className="mt-2 text-[18px] text-white/80">
                  — {service.quote[0].owner}
                </footer>
              </blockquote>
            )}

            <div className="hidden md:flex absolute right-25 bottom-40">
              <SocialIcons />
            </div>
            
          </div>

        </div>
      </div>

      {/* Scrollable content */}
      <div className="relative z-20 flex flex-col md:pl-[80px]">
        <div className="flex w-full h-[580px]"></div>

        {/* Section two */}
        <div className="flex flex-col md:flex-row w-full bg-white">
          <div className="flex items-start justify-start md:justify-center pl-5 md:pl-20 pt-10 md:pt-35 w-full md:w-[420px] bg-darkGrey">
            <h2 className="text-black font-semibold text-2xl md:text-3xl">
              {service.title}
            </h2>
          </div>
          <div className="flex-1 pt-5 md:pt-30 px-5 md:px-0 md:pr-15 md:pl-20 pb-15 bg-darkGrey">
            <h3 className="m-auto text-[18px] text-black whitespace-pre-line">
              {service.mainDescription}
            </h3>
          </div>
        </div>

        {/* Section three */}
        <div className="flex flex-col md:flex-row w-full bg-white">
          <div className="flex items-start justify-start pt-10 md:pt-20 pl-5 md:pl-15 w-full md:w-[420px]">
            <h2 className="text-black font-semibold text-2xl md:text-3xl">
              Other services covered in {service.title}
            </h2>
          </div>
          <div className="flex-1 py-10 md:py-15 px-5 md:px-0 md:pr-15 md:pl-20">
            <ul className="space-y-4">
              {service.keyOfferings.map((item, idx) => (
                <li key={idx} className="border-b border-gray-200 pb-3">
                  <button
                    onClick={() => toggleAccordion(idx)}
                    className="flex justify-between items-center w-full text-left"
                  >
                    <span className="text-[18px] font-medium text-black">
                      {item.title}
                    </span>
                    {openIndex === idx ? (
                      <Minus className="w-5 h-5 text-primary" />
                    ) : (
                      <Plus className="w-5 h-5 text-primary" />
                    )}
                  </button>

                  {openIndex === idx && (
                    <p className="mt-2 text-gray-700 text-[16px] leading-relaxed">
                      {item.description}
                    </p>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Section four: FAQs */}
        <div className="flex flex-col md:flex-row w-full bg-darkGrey">
          <div className="flex items-start justify-start pl-5 py-10 md:py-20 md:pl-15 w-full md:w-[420px]">
            <h2 className="text-black font-semibold text-2xl md:text-3xl">
              Frequently Asked Questions
            </h2>
          </div>
          <div className="flex-1 px-5 md:px-0 pb-5 md:pb-0 md:py-15 md:pr-15 md:pl-20">
            <ul className="space-y-4">
              {service.faqs.map((faq, idx) => (
                <li key={idx} className="border-b border-gray-200 pb-3">
                  <button
                    onClick={() =>
                      toggleAccordion(idx + service.keyOfferings.length)
                    } // offset index to avoid conflict
                    className="flex justify-between items-center w-full text-left"
                  >
                    <span className="text-[18px] font-medium text-black">
                      {faq.title}
                    </span>
                    {openIndex === idx + service.keyOfferings.length ? (
                      <Minus className="w-5 h-5 text-primary" />
                    ) : (
                      <Plus className="w-5 h-5 text-primary" />
                    )}
                  </button>

                  {openIndex === idx + service.keyOfferings.length && (
                    <p className="mt-2 text-gray-700 text-[16px] leading-relaxed">
                      {faq.description}
                    </p>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>

        
      </div>
    </div>
  );
}
